import React, { FC, useCallback } from "react";

import {
  ServiceTaskTypeEnum,
  TaskDriverStatusEnum,
} from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import { RightPanel } from "../components/RightPanel";
import { useJobQuery } from "../data/queries/jobQueries";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { formatDriverTaskType } from "../utils/strings";
import { DriverAddDumpReceiptPhoto } from "./DriverAddDumpReceiptPhoto";
import { DriverAddPhotoAfterCleanupStep } from "./DriverAddPhotoAfterCleanupStep";
import { DriverAddPhotoAreasCleaned } from "./DriverAddPhotoAreasCleaned";
import { DriverAddPhotoBeforeCleanupStep } from "./DriverAddPhotoBeforeCleanupStep";
import { DriverAddPhotoBinContents } from "./DriverAddPhotoBinContents";
import { DriverAddPhotoDroppedBin } from "./DriverAddPhotoDroppedBin";
import { DriverAddPhotoObstruction } from "./DriverAddPhotoObstruction";
import { DriverCanDropBin } from "./DriverCanDropBin";
import { DriverDriveToDump } from "./DriverDriveToDump";
import { DriverDrivingBackToSite } from "./DriverDrivingBackToSite";
import { DriverDumpTasksReview } from "./DriverDumpTasksReview";
import { DriverFillingBinContents } from "./DriverFillingBinContents";
import { DriverFillingFloorsCleaned } from "./DriverFillingFloorsCleaned";
import { DriverFillingInteriorCleanupType } from "./DriverFillingInteriorCleanupType";
import { DriverFillingReasonCantDropBin } from "./DriverFillingReasonCantDropBin";
import { DriverFillingSQFTCleaned } from "./DriverFillingSQFTCleaned";
import { DriverFillingSQFTPerFloor } from "./DriverFillingSQFTPerFloor";
import { DriverFillingWeightSurcharge } from "./DriverFillingWeightSurcharge";
import { DriverJunkLoadReviewScreen } from "./DriverJunkLoadReviewScreen";
import { DriverLoadSizeStep } from "./DriverLoadSizeStep";
import { DriverLocationAccessStep } from "./DriverLocationAccessStep";
import { DriverNeedAnotherLoadStep } from "./DriverNeedAnotherLoadStep";
import { DriverPickUpBinOnSite } from "./DriverPickUpBinOnSite";
import { DriverSelectBinForService } from "./DriverSelectBinForService";
import { DriverStepDumpAndReturnDrivingToDump } from "./DriverStepDumpAndReturnDrivingToDump";
import { DriverTaskDetails } from "./DriverTaskDetails";
import { DriverTaskReviewScreen } from "./DriverTaskReviewScreen";
import { DriverWaitBinFilled } from "./DriverWaitBinFilled";

type DriverTaskDetailsDrawerPropsType = JobServiceTaskBaseType & {
  onCloseDrawer?: () => void;
  isOpen: boolean;
};

const DriverTaskDetailsDrawer: FC<DriverTaskDetailsDrawerPropsType> = ({
  jobId,
  serviceId,
  taskId,
  onCloseDrawer,
  isOpen,
}) => {
  const { data: jobData } = useJobQuery({ jobId });
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });
  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });

  const { t } = useTranslator();

  const getContent = useCallback(() => {
    const shouldValidateFromTask =
      taskId !== ServiceTaskTypeEnum.JUNKREMOVAL &&
      taskId !== ServiceTaskTypeEnum.PICKUPBIN;

    const isServiceStarted = shouldValidateFromTask
      ? taskData?.started
      : serviceData?.started;

    const checkDriverFlowStatus = (statusToCheck: TaskDriverStatusEnum) => {
      return shouldValidateFromTask
        ? taskData?.driverFlowStatus === statusToCheck
        : serviceData?.driverFlowStatus === statusToCheck;
    };

    const isDumpAndReturnBinTask =
      taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN;

    const isDrivingToSite = checkDriverFlowStatus(
      TaskDriverStatusEnum.DRIVING_TO_SITE,
    );

    const isTakingPhotoBeforeCleanup = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTO_BEFORE_CLEANUP,
    );

    const isCleaningArea = checkDriverFlowStatus(
      TaskDriverStatusEnum.CLEANING_AREA,
    );

    const isFillingLoadSize = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_LOAD_SIZE,
    );

    const isFillingLocationAccess = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_LOCATION_ACCESS,
    );

    const isTakingPhotoAfterCleanup = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTO_AFTER_CLEANUP,
    );

    const isAskingForAnotherLoad = checkDriverFlowStatus(
      TaskDriverStatusEnum.ASKING_FOR_ANOTHER_LOAD,
    );

    const isFillingInteriorCleanupType = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_INTERIOR_CLEANUP_TYPE,
    );

    const isFillingSQFTCleaned = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_SQFT_CLEANED,
    );

    const isFillingFloorsCleaned = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_FLOORS_CLEANED,
    );

    const isFillingSQFTPerFloor = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_SQFT_PER_FLOOR,
    );

    const isTakingPhotosAreasCleaned = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTOS_AREAS_CLEANED,
    );

    const isReviewingTask = checkDriverFlowStatus(
      TaskDriverStatusEnum.TASK_REVIEW,
    );

    const isDrivingToDump = checkDriverFlowStatus(
      TaskDriverStatusEnum.DRIVING_TO_DUMP,
    );

    const isFillingWeightSurcharge = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
    );

    const isTakingPhotoDumpReceipt = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTO_DUMP_RECEIPT,
    );

    const isReviewingDumpTasks = checkDriverFlowStatus(
      TaskDriverStatusEnum.DUMP_TASKS_REVIEW,
    );

    const isAskingCanDropTheBin = checkDriverFlowStatus(
      TaskDriverStatusEnum.ASKING_CAN_DROP_BIN,
    );

    const isSelectingBinForService = checkDriverFlowStatus(
      TaskDriverStatusEnum.SELECTING_BIN_FOR_SERVICE,
    );

    const isTakingPhotoDroppedBin = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN,
    );

    const isFillingReasonCantDropBin = checkDriverFlowStatus(
      TaskDriverStatusEnum.FILLING_REASON_CANT_DROP_BIN,
    );

    const isTakingPhotoObstruction = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTO_OBSTRUCTION,
    );

    const isPickingUpBinOnSite = checkDriverFlowStatus(
      TaskDriverStatusEnum.DRIVER_PICK_UP_BIN_ON_SITE,
    );

    const isFillingBinContents = checkDriverFlowStatus(
      TaskDriverStatusEnum.CATEGORIZE_BIN_CONTENTS,
    );

    const isTakingPhotoBinContents = checkDriverFlowStatus(
      TaskDriverStatusEnum.TAKING_PHOTO_BIN_CONTENTS,
    );

    const isWaitingBinToBeFilled = checkDriverFlowStatus(
      TaskDriverStatusEnum.WAIT_BIN_FILLED,
    );

    const isDrivingBackToSite = checkDriverFlowStatus(
      TaskDriverStatusEnum.DRIVING_BACK_TO_SITE,
    );

    const isDrivingReviewingJunkLoad = checkDriverFlowStatus(
      TaskDriverStatusEnum.JUNK_LOAD_REVIEW,
    );

    if (isReviewingTask) {
      return (
        <DriverTaskReviewScreen
          jobId={jobId}
          serviceId={serviceId}
          taskId={taskId}
          onBottomButtonPress={onCloseDrawer}
        />
      );
    }

    if (isReviewingDumpTasks) {
      return (
        <DriverDumpTasksReview
          jobId={jobId}
          serviceId={serviceId}
          taskId={taskId}
          onBottomButtonPress={onCloseDrawer}
        />
      );
    }

    if (isDrivingReviewingJunkLoad) {
      return (
        <DriverJunkLoadReviewScreen
          jobId={jobId}
          serviceId={serviceId}
          taskId={taskId}
          onBottomButtonPress={onCloseDrawer}
        />
      );
    }

    let componentToBeCreated;

    switch (true) {
      // If didn't start yet OR is driving to site OR is cleaning the area, show task details screen
      case !isServiceStarted || isDrivingToSite || isCleaningArea:
        componentToBeCreated = DriverTaskDetails;
        break;

      case isTakingPhotoBeforeCleanup:
        componentToBeCreated = DriverAddPhotoBeforeCleanupStep;
        break;

      case isFillingLoadSize:
        componentToBeCreated = DriverLoadSizeStep;
        break;

      case isFillingLocationAccess:
        componentToBeCreated = DriverLocationAccessStep;
        break;

      case isTakingPhotoAfterCleanup:
        componentToBeCreated = DriverAddPhotoAfterCleanupStep;
        break;

      case isAskingForAnotherLoad:
        componentToBeCreated = DriverNeedAnotherLoadStep;
        break;

      case isFillingInteriorCleanupType:
        componentToBeCreated = DriverFillingInteriorCleanupType;
        break;

      case isFillingSQFTCleaned:
        componentToBeCreated = DriverFillingSQFTCleaned;
        break;

      case isFillingFloorsCleaned:
        componentToBeCreated = DriverFillingFloorsCleaned;
        break;

      case isFillingSQFTPerFloor:
        componentToBeCreated = DriverFillingSQFTPerFloor;
        break;

      case isTakingPhotosAreasCleaned:
        componentToBeCreated = DriverAddPhotoAreasCleaned;
        break;

      case isDrivingToDump && !isDumpAndReturnBinTask:
        componentToBeCreated = DriverDriveToDump;
        break;

      case isFillingWeightSurcharge:
        componentToBeCreated = DriverFillingWeightSurcharge;
        break;

      case isTakingPhotoDumpReceipt:
        componentToBeCreated = DriverAddDumpReceiptPhoto;
        break;

      case isAskingCanDropTheBin:
        componentToBeCreated = DriverCanDropBin;
        break;

      case isSelectingBinForService:
        componentToBeCreated = DriverSelectBinForService;
        break;

      case isTakingPhotoDroppedBin:
        componentToBeCreated = DriverAddPhotoDroppedBin;
        break;

      case isFillingReasonCantDropBin:
        componentToBeCreated = DriverFillingReasonCantDropBin;
        break;

      case isTakingPhotoObstruction:
        componentToBeCreated = DriverAddPhotoObstruction;
        break;

      case isPickingUpBinOnSite:
        componentToBeCreated = DriverPickUpBinOnSite;
        break;

      case isFillingBinContents:
        componentToBeCreated = DriverFillingBinContents;
        break;

      case isTakingPhotoBinContents:
        componentToBeCreated = DriverAddPhotoBinContents;
        break;

      case isWaitingBinToBeFilled:
        componentToBeCreated = DriverWaitBinFilled;
        break;

      case isDrivingToDump && isDumpAndReturnBinTask:
        componentToBeCreated = DriverStepDumpAndReturnDrivingToDump;
        break;

      case isDrivingBackToSite && isDumpAndReturnBinTask:
        componentToBeCreated = DriverDrivingBackToSite;
        break;
    }

    if (componentToBeCreated) {
      return React.createElement(componentToBeCreated, {
        jobId,
        serviceId,
        taskId,
      });
    }
  }, [serviceData, taskData, jobId, serviceId, taskId, onCloseDrawer]);

  if (!jobData) {
    return null;
  }

  const panelTitle = taskData?.needToDump
    ? t("driver_task_details_drawer.dump_task")
    : formatDriverTaskType(taskId);

  return (
    <RightPanel
      title={panelTitle}
      controlledOpen={isOpen}
      content={getContent()}
      onDrawerClosed={onCloseDrawer}
      backgroundColor="bg-gray-100"
    />
  );
};

export { DriverTaskDetailsDrawer };
